import Toast, { type ToastType } from '@/components/ui/Toast';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ToastConfig {
  id: string;
  message: string;
  type: ToastType;
}

let globalToasts: ToastConfig[] = [];
const listeners = new Set<(toasts: ToastConfig[]) => void>();

function emit() {
  for (const listener of listeners) {
    listener(globalToasts);
  }
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastConfig[]>(globalToasts);

  useEffect(() => {
    listeners.add(setToasts);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    globalToasts = [...globalToasts, { id, message, type }];
    emit();
  }, []);

  const hideToast = useCallback((id: string) => {
    globalToasts = globalToasts.filter((toast) => toast.id !== id);
    emit();
  }, []);

  const ToastContainer = useCallback(() => {
    return (
      <>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </>
    );
  }, [toasts, hideToast]);

  const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
  const warning = useCallback((message: string) => showToast(message, 'warning'), [showToast]);
  const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);

  return useMemo(
    () => ({
      showToast,
      success,
      error,
      warning,
      info,
      ToastContainer,
    }),
    [showToast, success, error, warning, info, ToastContainer]
  );
}
